import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  TaskScopeNameEnum,
} from 'graphql-common';
import useTabParam from '@lib/hooks/useTabParam';
import usePrevious from '@lib/hooks/usePrevious';
import Tasks from 'components/Tasks/Tasks';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import { useLocation } from 'react-router-dom';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { SelectOption } from '@lib/components/Select/types';
import {
  getOverviewTableColumns,
  getOverviewTableTabs,
  getTableActionsMenu,
} from './utils/tableHelpers';
import useActions from './utils/useActions';
import TasksOverviewFilter from './TasksOverviewFilter';
import TasksOverviewStateCounters from './TasksOverviewStateCounters';

export default function TasksOverview() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [tabParam, setTabParam] = useTabParam({
    defaultTab: TaskScopeNameEnum.DashboardAll,
  });
  const prevTabParam = usePrevious(tabParam);

  const {
    basePeriodEndParam,
    basePeriodStartParam,
    filterParams,
    isPeriodCustomized,
    onCreateIntervention,
    onResultStateChangeHandler,
    onUpdateFilterParams,
    onViewHandler,
    onViewIntervention,
    resetAllFilterParams,
    resetFilterParams,
    resetPeriodFilterParams,
    taskCounterData,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
    defaultMinDateObj,
    defaultMaxDateObj,
  } = useActions();

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) ===
    AccessLevelEnum.Read;

  const tableActionsMenu = getTableActionsMenu({
    t,
    onViewHandler,
    onCreateIntervention,
    onViewIntervention,
  });
  const tableColumns = getOverviewTableColumns({
    onResultStateChangeHandler,
    t,
    taskScope: (tasksLoading
      ? prevTabParam || tabParam
      : tabParam) as TaskScopeNameEnum,
  });
  const tableTabs = getOverviewTableTabs({ t, taskCounterData });

  const isFilterApplied =
    isPeriodCustomized ||
    (
      _get(filterParams, TasksFilterFieldNames.Filter, []) as {
        site: SelectOption;
      }[]
    ).filter(({ site }) => !!site?.value)?.length > 0;

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageHeader title={t('dashboard')} />
      <TasksOverviewFilter
        basePeriodEndParam={basePeriodEndParam}
        basePeriodStartParam={basePeriodStartParam}
        filterParams={filterParams}
        onUpdateFilterParams={onUpdateFilterParams}
        resetFilterParams={resetFilterParams}
        resetPeriodFilterParams={resetPeriodFilterParams}
        maxDate={defaultMaxDateObj}
        minDate={defaultMinDateObj}
      />
      <TasksOverviewStateCounters
        taskCounterData={taskCounterData}
        tasksError={tasksError}
        tasksFirstLoading={tasksFirstLoading}
      />
      <Tasks
        isFilterApplied={isFilterApplied}
        isViewOnly={isViewOnly}
        onResetFilter={resetAllFilterParams}
        pageSubTitle={t('active-tasks-results')}
        setTabParam={setTabParam}
        tabParam={tabParam}
        tableActionsMenu={tableActionsMenu}
        tableColumns={tableColumns}
        tableTabs={tableTabs}
        tasks={tasks}
        tasksError={tasksError}
        tasksFirstLoading={tasksFirstLoading}
        tasksLoading={tasksLoading}
        totalTasksCount={totalTasksCount}
      />
    </>
  );
}
